import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../components/BottomNav';
import { updateWeeklyRanking } from '../utils/leaderboardUtils';
import { isGuestUser } from '../utils/guestUtils';
import GuestAccountModal from '../components/GuestAccountModal';
import { dataCache, CACHE_DURATIONS } from '../utils/dataCache';
import { isCloudflare503Error, isNetworkError } from '../utils/connectionUtils';

interface User {
  id: string;
  email: string;
  full_name?: string;
  display_name?: string;
  created_at?: string;
  aud?: string;
  user_metadata?: {
    full_name?: string;
    name?: string;
    [key: string]: any;
  };
}

interface HomeScreenProps {
  user: User | null;
  onLogout: () => void;
}

interface UserStats {
  streak: number;
  xp: number;
  lessonsCompleted: number;
  dailyGoal: number;
  weeklyProgress: number;
  totalLessons: number;
}

interface ContinueLearning {
  lessonId: string;
  lessonTitle: string;
  chapterName: string;
  levelCode: string;
}

const HomeScreen: React.FC<HomeScreenProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();

  // Language content
  const content = {
    bn: {
      learningProgress: "শেখার অগ্রগতি",
      dayStreak: "দিনের ধারাবাহিকতা",
      totalXP: "মোট পয়েন্ট",
      lessonsDone: "সম্পূর্ণ পাঠ",
      lesson: "পাঠ",
      continue: "চালিয়ে যান",
      searchPlaceholder: "পাঠ খুঁজুন...",
      featuredLesson: "বিশেষ পাঠ",
      grammarBoost: "ব্যাকরণ\nবুস্ট",
      wordMastery: "শব্দ\nআয়ত্ত",
      practiceDaily: "নিয়মিত\nঅনুশীলন",
      allLesson: "সব পাঠ",
      quiz: "কুইজ",
      wordMeaning: "শব্দের অর্থ",
      oppositeWords: "বিপরীত শব্দ",
      conversations: "কথোপকথন",
      writingPractice: "লেখা\nঅনুশীলন",
      ebook: "ই-বুক",
    },
    it: {
      learningProgress: "Progresso Apprendimento",
      dayStreak: "Serie Giorni",
      totalXP: "Punti Totali",
      lessonsDone: "Lezioni Completate",
      lesson: "Lezione",
      continue: "Continua",
      searchPlaceholder: "Cerca Lezione...",
      featuredLesson: "Lezione in Evidenza",
      grammarBoost: "Spinta\nGrammatica",
      wordMastery: "Padronanza\nParole",
      practiceDaily: "Pratica\nGiornaliera",
      allLesson: "Tutte le Lezioni",
      quiz: "Quiz",
      wordMeaning: "Significato\nParole",
      oppositeWords: "Parole\nOpposte",
      conversations: "Conversazioni",
      writingPractice: "Pratica\nScrittura",
      ebook: "E-Book",
    },
    en: {
      learningProgress: "Learning Progress",
      dayStreak: "Day Streak",
      totalXP: "Total XP",
      lessonsDone: "Lessons Done",
      lesson: "Lesson",
      continue: "Continue",
      searchPlaceholder: "Search Lesson...",
      featuredLesson: "Featured Lesson",
      grammarBoost: "Grammar\nBoost",
      wordMastery: "Word\nMastery",
      practiceDaily: "Practice\nDaily",
      allLesson: "All Lessons",
      quiz: "Quiz",
      wordMeaning: "Word\nMeaning",
      oppositeWords: "Opposite\nWords",
      conversations: "Conversations",
      writingPractice: "Writing\nPractice",
      ebook: "E-Book",
    }
  };
  const [stats, setStats] = useState<UserStats>({ 
    streak: 0, 
    xp: 0, 
    lessonsCompleted: 0, 
    dailyGoal: 100,
    weeklyProgress: 0,
    totalLessons: 0
  });
  const [continueLesson, setContinueLesson] = useState<ContinueLearning | null>(null);
  const [userProfile, setUserProfile] = useState<{ display_name?: string; full_name?: string } | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<'bn' | 'it' | 'en'>('bn');
  const [loading, setLoading] = useState(false); // Start with false - load from cache first
  const [showGuestModal, setShowGuestModal] = useState<boolean>(false);
  const [hasCheckedModal, setHasCheckedModal] = useState<boolean>(false);
  
  // Check if user has dismissed the modal before
  const hasDismissedModal = () => {
    return localStorage.getItem('guestModalDismissed') === 'true';
  };
  
  // Check if modal was shown recently (within last 24 hours)
  const wasModalShownRecently = () => {
    const lastShown = localStorage.getItem('guestModalLastShown');
    if (!lastShown) return false;
    
    const lastShownTime = parseInt(lastShown, 10);
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    return (now - lastShownTime) < twentyFourHours;
  };
  
  const dismissModal = () => {
    localStorage.setItem('guestModalDismissed', 'true');
    localStorage.setItem('guestModalLastShown', Date.now().toString());
    setShowGuestModal(false);
  };
  
  const showModal = () => {
    localStorage.setItem('guestModalLastShown', Date.now().toString());
    setShowGuestModal(true);
  };

  useEffect(() => {
    // Load saved language preference first (synchronous)
    const savedLanguage = localStorage.getItem('preferredLanguage') as 'bn' | 'it' | 'en';
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Try to load from cache first for instant display
    const cacheKey = user?.id ? `stats_${user.id}` : 'stats_guest';
    const cachedStats = dataCache.get<UserStats>(cacheKey);
    if (cachedStats) {
      setStats(cachedStats);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // Load data (will use cache if available)
    loadUserStats();
    loadContinueLearning();
    loadUserProfile();
    
    // Update weekly ranking when user loads home screen (async, non-blocking)
    if (user?.id && user.id !== 'guest') {
      updateWeeklyRanking(user.id).catch(err => {
        console.error('Failed to update weekly ranking:', err);
      });
    }

    // Check if guest should be prompted to create account
    if (!hasCheckedModal && isGuestUser() && !hasDismissedModal() && !wasModalShownRecently()) {
      setHasCheckedModal(true);
      const timer = setTimeout(() => {
        if (isGuestUser() && !hasDismissedModal() && !wasModalShownRecently()) {
          showModal();
        }
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setHasCheckedModal(true);
    }
  }, [user?.id]);


  // Reload stats when user returns to home screen
  useEffect(() => {
    const handleFocus = () => {
      loadUserStats();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadUserStats = async () => {
    try {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      const cacheKey = `stats_${user.id}`;

      // If guest user, load from localStorage (no cache needed)
      if (user.id === 'guest') {
        try {
          const progressKey = 'userProgress';
          const existingProgress = localStorage.getItem(progressKey);
          const progress = existingProgress ? JSON.parse(existingProgress) : {
            completedLessons: [],
            totalXP: 0,
            streak: 0
          };

          const guestStats: UserStats = {
            streak: progress.streak || 0,
            xp: progress.totalXP || 0,
            lessonsCompleted: progress.completedLessons?.length || 0,
            dailyGoal: 100,
            weeklyProgress: 0,
            totalLessons: 25
          };

          setStats(guestStats);
        } catch (error) {
          console.error('Error loading guest stats:', error);
        }
        setLoading(false);
        return;
      }

      // Check cache first
      const cachedStats = dataCache.get<UserStats>(cacheKey);
      if (cachedStats) {
        setStats(cachedStats);
        setLoading(false);
        // Still refresh in background (non-blocking)
        setTimeout(() => refreshUserStats(), 100);
        return;
      }

      // Load from database
      await refreshUserStats();

    } catch (error) {
      console.error('Error loading stats:', error);
      setLoading(false);
    }
  };

  const refreshUserStats = async () => {
    if (!user?.id || user.id === 'guest') return;

    try {
      // Get total lessons count from database (cache this separately)
      const totalLessonsCacheKey = 'total_lessons_count';
      let totalLessons = dataCache.get<number>(totalLessonsCacheKey);
      
      if (!totalLessons) {
        const { count } = await supabase
        .from('Lessons')
        .select('*', { count: 'exact', head: true });
        totalLessons = count || 0;
        dataCache.set(totalLessonsCacheKey, totalLessons, CACHE_DURATIONS.LEVELS);
      }

      // Get user progress from database
      const { data: userData } = await supabase
        .from('users')
        .select('xp, streak, display_name, full_name')
        .eq('id', user.id)
        .single();

      // Get completed lessons count from QuizAttempts
      const { data: quizAttempts } = await supabase
        .from('QuizAttempts')
        .select('lesson_id, score, total_questions')
        .eq('user_id', user.id);

      // Count distinct lessons where user passed (scored >= 50%)
      const completedLessons = new Set(
        (quizAttempts || [])
          .filter((attempt: any) => attempt.score >= (attempt.total_questions / 2))
          .map((attempt: any) => attempt.lesson_id)
      );

      // Calculate weekly progress
      const weeklyProgress = completedLessons.size > 0 ? Math.min(completedLessons.size % 7, 7) : 0;

      const newStats: UserStats = {
        streak: userData?.streak || 0,
        xp: userData?.xp || 0,
        lessonsCompleted: completedLessons.size || 0,
        dailyGoal: 100,
        weeklyProgress: weeklyProgress,
        totalLessons: totalLessons || 0,
      };

      setStats(newStats);
      
      // Cache the stats
      const cacheKey = `stats_${user.id}`;
      dataCache.set(cacheKey, newStats, CACHE_DURATIONS.STATS);
      
      setLoading(false);
    } catch (error) {
      console.error('Error refreshing stats:', error);
      setLoading(false);
      // Set default values if there's an error
      setStats({
        streak: 0,
        xp: 0,
        lessonsCompleted: 0,
        dailyGoal: 100,
        weeklyProgress: 0,
        totalLessons: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadContinueLearning = async () => {
    try {
      // Get the first lesson from the first chapter of the first level
      const { data: lesson, error } = await supabase
        .from('Lessons')
        .select(`
          id,
          title,
          Chapters!inner(
            name,
            CourseLevels!inner(level_code)
          )
        `)
        .order('order_index')
        .limit(1)
        .single();

      if (lesson && !error && lesson.Chapters) {
        const chapter = Array.isArray(lesson.Chapters) ? lesson.Chapters[0] : lesson.Chapters;
        const levelData = chapter?.CourseLevels;
        const level = Array.isArray(levelData) ? levelData[0] : levelData;
        
        setContinueLesson({
          lessonId: lesson.id,
          lessonTitle: lesson.title,
          chapterName: chapter?.name || 'Chapter',
          levelCode: level?.level_code || 'A1',
        });
      }
    } catch (error) {
      console.error('Error loading continue learning:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      if (!user?.id) return;
      // Guests don't have a row in `users`; their id is the literal string "guest"
      // (not a UUID), so PostgREST rejects the request with HTTP 400.
      if (isGuestUser() || user.id === 'guest') return;

      // Fetch user profile from our custom users table
      const { data: profile, error } = await supabase
        .from('users')
        .select('display_name, full_name, email')
        .eq('id', user.id)
        .single();

      if (profile && !error) {
        setUserProfile(profile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    const greetings = {
      bn: hour < 12 ? 'সুপ্রভাত' : hour < 18 ? 'শুভ বিকাল' : 'শুভ সন্ধ্যা',
      it: hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera',
      en: hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
    };
    return greetings[currentLanguage];
  };


  const getUserDisplayName = () => {
    // Priority: display_name > full_name > user_metadata > first name from email > email username
    const name = userProfile?.display_name || 
                 userProfile?.full_name || 
                 user?.user_metadata?.full_name ||
                 user?.user_metadata?.name ||
                 user?.full_name || 
                 user?.email?.split('@')[0] || 
                 'User';
    
    // If it's an email username, try to make it look better
    if (name.includes('@')) {
      return name.split('@')[0];
    }
    
    // Get first name only if full name
    const parts = name.split(' ');
    return parts[0]; // Return just first name for cleaner UI
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src="logo512.png" alt="Shikhun Italian" />
        </div>
      </div>
    );
  }

  return (
    <div className="home-screen">
      {/* Header */}
      <div className="screen-header home-header">
        <div 
          className="user-avatar-home"
          onClick={() => navigate('/profile')}
          style={{ cursor: 'pointer' }}
        >
          <div className="avatar-home">
            {getUserDisplayName().charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="user-greeting-home">
          <h2 className="user-name-text">{getGreeting()}, {getUserDisplayName()}</h2>
        </div>
        <button 
          className="header-btn trophy-btn"
          onClick={() => navigate('/leaderboard')}
          aria-label="View Leaderboard"
        >
          <span className="trophy-icon-reversed">🏆</span>
        </button>
      </div>

      {/* Learning Progress Card */}
      <div className="learning-progress-card">
        <div className="progress-header">
          <h3>{content[currentLanguage].learningProgress}</h3>
        </div>
        
        <div className="stats-row">
          <div className="stat-item">
            <div className="stat-number">{stats.streak}</div>
            <div className="stat-label">{content[currentLanguage].dayStreak}</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{stats.xp}</div>
            <div className="stat-label">{content[currentLanguage].totalXP}</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{stats.lessonsCompleted}</div>
            <div className="stat-label">{content[currentLanguage].lessonsDone}</div>
          </div>
        </div>
        
        <div className="current-lesson">
          <div className="lesson-info">
            <span className="flag">🇮🇹</span>
            <span className="language">Italian</span>
            <span className="lesson-count">{stats.lessonsCompleted}/{stats.totalLessons} {content[currentLanguage].lesson}</span>
          </div>
          <button className="continue-btn" onClick={() => navigate('/levels')}>
            {content[currentLanguage].continue}
          </button>
        </div>
      </div>

      {/* Featured Lesson */}
      <div className="featured-section">
        <h3>{content[currentLanguage].featuredLesson}</h3>
        <div className="featured-cards">
          {/* Word Meaning - 50% width */}
          <div className="featured-card ebook" onClick={() => navigate('/ebooks')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].ebook}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">📚</div>
          </div>

          <div className="featured-card word-meaning" onClick={() => navigate('/word-meaning')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].wordMeaning}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">📖</div>
          </div>

          {/* Opposite Words - 50% width */}
          <div className="featured-card opposite-words" onClick={() => navigate('/opposite-words')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].oppositeWords}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">🔄</div>
          </div>

          {/* Conversations - 50% width */}
          <div className="featured-card conversations" onClick={() => navigate('/conversations')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].conversations}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">💬</div>
          </div>

          <div className="featured-card grammar" onClick={() => navigate('/grammar')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].grammarBoost}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">📖</div>
          </div>
          
          <div className="featured-card words" onClick={() => navigate('/words')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].wordMastery}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">📚</div>
          </div>
          
          <div className="featured-card practice" onClick={() => navigate('/levels')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].practiceDaily}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">⏰</div>
          </div>
          
          <div className="featured-card mastery" onClick={() => navigate('/quizzes')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].quiz}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">📝</div>
          </div>

          <div className="featured-card writing" onClick={() => navigate('/writing')}>
            <div className="card-header">
              <span className="card-title">{content[currentLanguage].writingPractice}</span>
              <span className="heart-icon">♡</span>
            </div>
            <div className="card-icon">✍️</div>
          </div>

          
        </div>
      </div>

      {/* Guest Account Modal */}
      {isGuestUser() && (
        <GuestAccountModal
          isOpen={showGuestModal}
          onClose={dismissModal}
          guestProgress={{
            lessonsCompleted: stats.lessonsCompleted,
            xp: stats.xp,
            streak: stats.streak
          }}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default HomeScreen;
